import React, { useCallback, useState, useRef } from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    MenuItem,
    Select,
    TextField,
    LinearProgress,
    Typography,
    Box
} from "@material-ui/core";
import { makeStyles, Theme } from "@material-ui/core/styles";
import { CloudUpload, PhotoCamera } from "@material-ui/icons";
import { useSnackbar } from "notistack";

import { PhotoUploadRequest, PHOTO_CATEGORIES, PHOTO_CATEGORY_LABELS, PhotoCategory } from "../../../../../dtos/sitePhoto";
import { AjaxResult } from "../../../../../enums/ajaxResult";
import { useApi } from "../../../../useApi";

const useStyles = makeStyles((theme: Theme) => ({
    uploadArea: {
        border: "2px dashed #ccc",
        borderRadius: "8px",
        padding: "40px 20px",
        textAlign: "center",
        cursor: "pointer",
        transition: "border-color 0.3s ease",
        "&:hover": {
            borderColor: theme.palette.primary.main,
        },
        "&.dragOver": {
            borderColor: theme.palette.primary.main,
            backgroundColor: theme.palette.primary.light + "20",
        }
    },
    uploadIcon: {
        fontSize: "48px",
        color: "#ccc",
        marginBottom: "16px",
    },
    hiddenInput: {
        display: "none",
    },
    formField: {
        margin: "16px 0",
        width: "100%",
    },
    progressContainer: {
        margin: "16px 0",
    },
    previewImage: {
        maxWidth: "100%",
        maxHeight: "200px",
        margin: "16px 0",
        borderRadius: "4px",
    },
    descriptionField: {
        "& .MuiInputBase-root": {
            minHeight: "80px",
            resize: "vertical",
        },
        "& .MuiInputBase-input": {
            minHeight: "60px",
            resize: "vertical",
        }
    }
}));

interface Props {
    siteId: number;
    open: boolean;
    onClose: () => void;
    onUploadSuccess: () => void;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp", "image/bmp"];

export const PhotoUpload: React.FC<Props> = ({ siteId, open, onClose, onUploadSuccess }) => {
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const { sitePhotoApi } = useApi();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [category, setCategory] = useState<PhotoCategory>("uncategorized");
    const [description, setDescription] = useState("");
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [descriptionError, setDescriptionError] = useState<string | null>(null);

    const MAX_DESCRIPTION_LENGTH = 500;
    const getCharacterCount = (value: string): number => {
        const anyIntl: any = Intl as any;
        if (typeof anyIntl !== "undefined" && anyIntl.Segmenter) {
            const segmenter = new anyIntl.Segmenter(undefined, { granularity: "grapheme" });
            return Array.from(segmenter.segment(value)).length;
        }
        return Array.from(value).length;
    };

    const validateFile = (file: File): string | null => {
        if (!ALLOWED_TYPES.includes(file.type)) {
            return "File type not supported. Please upload JPEG, PNG, GIF, WebP, or BMP images.";
        }
        if (file.size > MAX_FILE_SIZE) {
            return "File size must be less than 10MB.";
        }
        return null;
    };

    const handleFileSelect = (file: File) => {
        const error = validateFile(file);
        if (error) {
            enqueueSnackbar(error, { variant: "error" });
            return;
        }

        setSelectedFile(file);

        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
        }

        const url = URL.createObjectURL(file);
        setPreviewUrl(url);

        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files && event.target.files[0];
        if (file) {
            handleFileSelect(file);
        }
    };

    const handleDragOver = useCallback((event: React.DragEvent) => {
        event.preventDefault();
        setDragOver(true);
    }, []);

    const handleDragLeave = useCallback((event: React.DragEvent) => {
        event.preventDefault();
        setDragOver(false);
    }, []);

    const handleDrop = (event: React.DragEvent) => {
        event.preventDefault();
        setDragOver(false);

        const files = event.dataTransfer.files;
        if (files.length > 0) {
            if (files.length > 1) {
                enqueueSnackbar("Only one file at a time can be uploaded", { variant: "warning" });
            }
            handleFileSelect(files[0]);
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            enqueueSnackbar("Please select a file to upload", { variant: "error" });
            return;
        }
        const count = getCharacterCount(description);
        if (count > MAX_DESCRIPTION_LENGTH) {
            setDescriptionError(`Description must be ${MAX_DESCRIPTION_LENGTH} characters or less`);
            enqueueSnackbar(`Description must be ${MAX_DESCRIPTION_LENGTH} characters or less`, { variant: "error" });
            return;
        }

        setUploading(true);

        try {
            const uploadData: PhotoUploadRequest = {
                file: selectedFile,
                category: category,
                description: description.trim() || undefined,
            };

            const result = await sitePhotoApi.uploadPhoto(siteId, uploadData);

            if (result.result === AjaxResult.Success) {
                enqueueSnackbar("Photo uploaded successfully", { variant: "success" });
                onUploadSuccess();
                handleClose();
            } else if (result.messages) {
                enqueueSnackbar(result.messages.join(", "), { variant: "error" });
            } else {
                enqueueSnackbar("Failed to upload photo", { variant: "error" });
            }
        } catch (error) {
            console.error("Upload error:", error);
            enqueueSnackbar("Failed to upload photo", { variant: "error" });
        } finally {
            setUploading(false);
        }
    };

    const handleClose = () => {
        if (uploading) return;

        setSelectedFile(null);
        setCategory("uncategorized");
        setDescription("");
        setDescriptionError(null);
        setDragOver(false);
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box display="flex" alignItems="center">
                    <PhotoCamera style={{ marginRight: "8px" }} />
                    Upload Site Photo
                </Box>
            </DialogTitle>

            <DialogContent>
                <div
                    className={`${classes.uploadArea} ${dragOver ? "dragOver" : ""}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => {
                        fileInputRef.current && fileInputRef.current.click();
                    }}
                >
                    <CloudUpload className={classes.uploadIcon} />
                    <Typography variant="h6" gutterBottom>
                        {selectedFile ? selectedFile.name : "Drop photo here or click to browse"}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        Max 10MB • JPEG, PNG, GIF, WebP, BMP
                    </Typography>
                </div>

                <input
                    ref={fileInputRef}
                    id="photo-upload-input"
                    type="file"
                    accept="image/*"
                    onChange={handleInputChange}
                    className={classes.hiddenInput}
                />

                {previewUrl && (
                    <img src={previewUrl} alt="Preview" className={classes.previewImage} />
                )}

                <FormControl className={classes.formField}>
                    <Typography variant="body2" gutterBottom>Category</Typography>
                    <Select
                        value={category}
                        onChange={(e) => setCategory(e.target.value as PhotoCategory)}
                        variant="outlined"
                    >
                        {PHOTO_CATEGORIES.map((cat) => (
                            <MenuItem key={cat} value={cat}>
                                {PHOTO_CATEGORY_LABELS[cat]}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl className={classes.formField}>
                    <Typography variant="body2" gutterBottom>Description (Optional)</Typography>
                    <TextField
                        value={description}
                        onChange={(e) => {
                            const value = e.target.value;
                            setDescription(value);
                            const count = getCharacterCount(value);
                            if (count > MAX_DESCRIPTION_LENGTH) {
                                setDescriptionError(`Description must be ${MAX_DESCRIPTION_LENGTH} characters or less`);
                            } else {
                                setDescriptionError(null);
                            }
                        }}
                        multiline
                        rows={3}
                        variant="outlined"
                        placeholder="Describe what this photo shows..."
                        className={classes.descriptionField}
                        error={!!descriptionError}
                        helperText={descriptionError || `${getCharacterCount(description)}/${MAX_DESCRIPTION_LENGTH} characters`}
                    />
                </FormControl>

                {uploading && (
                    <div className={classes.progressContainer}>
                        <Typography variant="body2" gutterBottom>
                            Uploading...
                        </Typography>
                        <LinearProgress />
                    </div>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} disabled={uploading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleUpload}
                    color="primary"
                    variant="contained"
                    disabled={!selectedFile || uploading}
                >
                    {uploading ? "Uploading..." : "Upload"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}; 